import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Info } from "lucide-react";
import api from "../lib/api";
import { CashNotice, CategoryList, Examples } from "../components/EarningsExplainer";

function Row({ label, value, hint, testid, tone = "text-[#FBF8F3]" }) {
  return (
    <div className="flex items-center justify-between py-3 border-t border-white/8 first:border-t-0" data-testid={testid}>
      <div><div className="text-sm text-[#D8D2C9]">{label}</div>{hint && <div className="text-xs text-[#B5AEA4] leading-snug">{hint}</div>}</div>
      <div className={`font-serif text-xl ${tone}`}>{value}</div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="db-glass rounded-3xl border border-[#D4AF37]/20 p-6" data-testid={testid}>
      <h2 className="font-serif text-xl text-[#FBF8F3] mb-2">{title}</h2>{children}
    </div>
  );
}

export default function Earnings() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [x, setX] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/earnings/summary"), api.get("/earnings/explainer")])
      .then(([a, b]) => { setD(a.data); setX(b.data); }).catch(() => setErr(true));
  }, []);
  if (err) return <div className="p-8 text-[#E58CA0]" data-testid="earnings-error">Could not load your earnings summary. Please try again shortly.</div>;
  if (!d || !x) return <div className="p-8 text-[#9E978E]">Loading…</div>;
  const b = d.balances;
  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="earnings-page">
      <button onClick={() => navigate("/wallet")} className="flex items-center gap-1 text-sm text-[#D4AF37]" data-testid="earnings-back-btn"><ArrowLeft size={15} /> Wallet</button>
      <div><div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">Earnings Summary</div>
        <h1 className="font-serif text-4xl text-[#FBF8F3] mt-1">Your credits, by source</h1></div>
      <CashNotice text={x.cash_redemption.notice} />

      <Card title="Balances" testid="earnings-balances">
        <Row label="Purchased credits" hint="Bought with a card. Not cash-redeemable." value={b.purchased} testid="earnings-purchased" />
        <Row label="Promotional credits" hint="Welcome and promo bonuses. Not cash-redeemable." value={b.promotional} testid="earnings-promotional" />
        <Row label="Earned spendable credits" hint="All earned credits you can spend on DateBid." value={b.earned_spendable} testid="earnings-earned-spendable" tone="text-[#6FD79E]" />
        <p className="text-[11px] text-[#9E978E] mt-3" data-testid="earnings-spend-order">Spending order: {d.spend_order.join(" → ")}.</p>
      </Card>

      <CategoryList x={x} />
      <Examples x={x} />

      <Card title="Activity" testid="earnings-activity">
        <Row label="Earned from messaging unlocks" value={d.lifetime_earned.unlock_rewards} testid="earnings-lifetime-unlock" />
        <Row label="Earned from gifts received" value={d.lifetime_earned.gifts_received} testid="earnings-lifetime-gifts" />
        <Row label="Counteroffer spending" hint={d.counteroffer_spending.refunded ? `${d.counteroffer_spending.refunded} returned` : undefined} value={d.counteroffer_spending.net} testid="earnings-counter-spend" />
        <Row label="Rewards under review" value={d.reward_reviews.under_review} testid="earnings-rewards-under-review" />
      </Card>

      <Card title="Cash redemption" testid="earnings-redemption">
        <p className="text-sm text-[#B5AEA4]" data-testid="earnings-redemption-unavailable">Cash redemption is not available and no payout can be requested. Cash-eligible figures do not guarantee any payment.</p>
        <p className="text-[11px] text-[#9E978E] mt-3 flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" /> Only earned credits that qualify are ever cash-eligible. Purchased and promotional credits are never cash-redeemable.</p>
      </Card>
    </div>
  );
}
